import { presentPanTiles } from './fractalPixels';
import { iterateUntilEscape, iterateWithDistance } from './fractalIteration';
import { samplePalette } from './fractalPalette';
import { canCertifyCpuPalette } from './cpuExactPan';
import { fullConjugateRowMap } from './fractalConjugateRows';

const iterResult = { iterations: 0, mu: 0 };

// Reusable object for colorFunc to avoid allocation in hot loop
const colorInput = { numIterations: 0, mu: 0 };
const clampByte = value => value <= 0 ? 0 : value >= 255 ? 255 : value | 0;
const packRgba32 = color =>
  clampByte(color[0]) | (clampByte(color[1]) << 8) | (clampByte(color[2]) << 16) | (clampByte(color[3]) << 24);

/**
 * Given a canvas element, draw a fractal using the escape time method
 * Highly optimized:
 * - Precomputed scale factors
 * - Reusable objects to avoid allocations
 * - Inlined pixel writes
 */
export function drawJuliaFractal(canvasEl, xRange, yRange, colorFunc, useMu = true, paletteParams = null) {
  const canvasWidth = canvasEl.width;
  const canvasHeight = canvasEl.height;
  const context = canvasEl.getContext('2d');
  const canvasImageData = context.createImageData(canvasWidth, canvasHeight);
  const image = canvasImageData.data;
  const image32 = new Uint32Array(image.buffer);
  
  // Precompute scale factors to avoid division in inner loop
  const xScale = (xRange[1] - xRange[0]) / canvasWidth;
  const yScale = (yRange[1] - yRange[0]) / canvasHeight;
  const xBase = xRange[0];
  const yBase = yRange[0];
  
  const result = iterResult;
  const juliaC = paletteParams?.juliaC ?? colorFunc?.juliaC ?? null;
  const useDem = (paletteParams?.demStrength ?? 0) > 0;
  const reusablePaletteColor = paletteParams ? [0, 0, 0, 0] : null;
  // A custom callback may depend on call order, so only the built-in plain
  // palette path can reuse a complete conjugate row without changing it.
  const paletteDescriptors = paletteParams && Object.getPrototypeOf(paletteParams) === Object.prototype
    ? Object.getOwnPropertyDescriptors(paletteParams) : null;
  const stablePalette = paletteDescriptors && Object.values(paletteDescriptors).every(descriptor =>
    Object.prototype.hasOwnProperty.call(descriptor, 'value')) &&
    canCertifyCpuPalette(paletteParams);
  const conjugateRows = stablePalette ? fullConjugateRowMap({ canvasWidth, canvasHeight,
    xRange, yRange, colorParams: paletteParams }) : null;
  
  for (let i = 0; i < canvasHeight; i++) {
    const y0 = yBase + (i + 0.5) * yScale;
    const rowStart32 = i * canvasWidth;
    const partner = conjugateRows?.partners[i] ?? -1;
    if (partner >= 0) {
      const partnerStart32 = partner * canvasWidth;
      if (conjugateRows.antipodal) {
        for (let j = 0; j < canvasWidth; j++) {
          image32[rowStart32 + j] = image32[partnerStart32 + canvasWidth - 1 - j];
        }
      } else {
        image32.copyWithin(rowStart32, partnerStart32, partnerStart32 + canvasWidth);
      }
      continue;
    }
    
    for (let j = 0; j < canvasWidth; j++) {
      const x0 = xBase + (j + 0.5) * xScale;
      if (useDem) iterateWithDistance(x0, y0, result, undefined, useMu, juliaC);
      else iterateUntilEscape(x0, y0, result, undefined, useMu, juliaC);
      
      if (result.escaped) {
        let color;
        if (paletteParams) {
          color = samplePalette(paletteParams.useMu === false ? result.iterations : result.mu,
            paletteParams, reusablePaletteColor, useDem ? result.dem : 0);
        } else {
          colorInput.numIterations = result.iterations;
          colorInput.mu = result.mu;
          if (useDem) colorInput.dem = result.dem;
          color = colorFunc(colorInput);
        }
        image32[rowStart32 + j] = packRgba32(color);
      }
    }
  }
  context.putImageData(canvasImageData, 0, 0);
}

/**
 * Check if a range change is a pure pan (same scale, different offset)
 */
function isPanOnly(oldXRange, oldYRange, newXRange, newYRange) {
  const oldXSpan = oldXRange[1] - oldXRange[0];
  const oldYSpan = oldYRange[1] - oldYRange[0];
  const newXSpan = newXRange[1] - newXRange[0];
  const newYSpan = newYRange[1] - newYRange[0];
  
  // Check if spans are equal (within floating point tolerance)
  const tolerance = 1e-10;
  return Math.abs(oldXSpan - newXSpan) < tolerance && 
         Math.abs(oldYSpan - newYSpan) < tolerance;
}

function renderPanRegion(x, y, width, height, xBase, yBase, xScale, yScale, colorFunc, useMu, juliaC = null, useDem = false, paletteParams = null) {
  const pixels = new Uint8ClampedArray(width * height * 4);
  const pixels32 = new Uint32Array(pixels.buffer);
  const reusablePaletteColor = paletteParams ? [0, 0, 0, 0] : null;
  for (let row = 0; row < height; row++) {
    const y0 = yBase + (y + row + 0.5) * yScale;
    for (let col = 0; col < width; col++) {
      const x0 = xBase + (x + col + 0.5) * xScale;
      if (useDem) iterateWithDistance(x0, y0, iterResult, undefined, useMu, juliaC);
      else iterateUntilEscape(x0, y0, iterResult, undefined, useMu, juliaC);
      if (!iterResult.escaped) continue;
      let color;
      if (paletteParams) {
        color = samplePalette(paletteParams.useMu === false ? iterResult.iterations : iterResult.mu,
          paletteParams, reusablePaletteColor, useDem ? iterResult.dem : 0);
      } else {
        colorInput.numIterations = iterResult.iterations;
        colorInput.mu = iterResult.mu;
        if (useDem) colorInput.dem = iterResult.dem;
        color = colorFunc(colorInput);
      }
      pixels32[row * width + col] = packRgba32(color);
    }
  }
  return { x, y, width, height, pixels };
}

/**
 * Apply a pan to existing canvas content and render only new edges
 * Returns true if pan was applied, false if full re-render needed
 * 
 * Work is limited to exposed strips. The canvas backend moves retained pixels
 * so no full-canvas readback or JS pixel copy is needed.
 */
export function applyPanAndRenderEdges(canvasEl, oldXRange, oldYRange, newXRange, newYRange, colorFunc, useMu = true, paletteParams = null) {
  if (!isPanOnly(oldXRange, oldYRange, newXRange, newYRange)) return false;
  const canvasWidth = canvasEl.width;
  const canvasHeight = canvasEl.height;
  const context = canvasEl.getContext('2d');
  const xScale = (newXRange[1] - newXRange[0]) / canvasWidth;
  const yScale = (newYRange[1] - newYRange[0]) / canvasHeight;
  const pixelOffsetX = Math.round((newXRange[0] - oldXRange[0]) / xScale);
  const pixelOffsetY = Math.round((newYRange[0] - oldYRange[0]) / yScale);
  if (!Number.isFinite(pixelOffsetX) || !Number.isFinite(pixelOffsetY) ||
      Math.abs(pixelOffsetX) > canvasWidth * 0.8 ||
      Math.abs(pixelOffsetY) > canvasHeight * 0.8) return false;
  if (pixelOffsetX === 0 && pixelOffsetY === 0) return true;
  const xBase = newXRange[0];
  const yBase = newYRange[0];
  const juliaC = paletteParams?.juliaC ?? colorFunc?.juliaC ?? null;
  const useDem = (paletteParams?.demStrength ?? colorFunc?.demStrength ?? 0) > 0;
  const regions = [];
  if (pixelOffsetX !== 0) {
    const stripWidth = Math.abs(pixelOffsetX);
    const stripX = pixelOffsetX > 0 ? canvasWidth - stripWidth : 0;
    regions.push(renderPanRegion(stripX, 0, stripWidth, canvasHeight,
      xBase, yBase, xScale, yScale, colorFunc, useMu, juliaC, useDem, paletteParams));
  }
  if (pixelOffsetY !== 0) {
    const stripHeight = Math.abs(pixelOffsetY);
    const stripY = pixelOffsetY > 0 ? canvasHeight - stripHeight : 0;
    const stripX = Math.max(0, -pixelOffsetX);
    regions.push(renderPanRegion(stripX, stripY, canvasWidth - Math.abs(pixelOffsetX), stripHeight,
      xBase, yBase, xScale, yScale, colorFunc, useMu, juliaC, useDem, paletteParams));
  }
  // Complete numerical work before changing visible canvas pixels.
  presentPanTiles(canvasEl, context, { pixelOffsetX, pixelOffsetY, regions });
  return true;
}
